// ============================================================
// EWFL Player Career Mode — simulation engine
// - Player plays every match for their team
// - During their team's matches, player makes decisions
// - Other teams' matches are auto-simulated
// ============================================================
import { getTeamOverall, EMPTY_COMP_STATS, EMPTY_STATS, getSlotPositionFor, getPositionCompatibility, calculateStandings, generateSeasonFixtures, START_YEAR } from './gameData';
import { quickSimulate, updateTeamStats, updatePlayerStats, applyMatchGrowth } from './simulationEngine';
import { buildChampionsLeagueTeams, buildEuropaLeagueTeams, buildConferenceLeagueTeams, buildCWCTeams } from './realTeams';

// ============================================================
// Decision definitions - shown to user during their match
// Each decision has 3 options each with a different risk/reward profile
// that influences the player's match rating + event probabilities.
// ============================================================
export const DECISION_TEMPLATES = [
  // ==================== ATTACKING SCENARIOS ====================
  {
    id: 'attacking_third',
    minute: [10, 35],
    scenario: "You have broken into the attacking third with a teammate on the overlap. What do you do?",
    options: [
      { key: 'shoot', label: 'Shoot from range', risk: 'high', shot: 0.55, assistChance: 0, rating: { success: 1.1, fail: -0.6 } },
      { key: 'pass', label: 'Pass to the runner', risk: 'low', shot: 0, assistChance: 0.55, rating: { success: 0.85, fail: -0.15 } },
      { key: 'dribble', label: 'Cut inside and dribble', risk: 'med', shot: 0.35, assistChance: 0.15, rating: { success: 0.95, fail: -0.4 } }
    ]
  },
  {
    id: 'one_on_one',
    minute: [45, 80],
    scenario: '1-on-1 with the keeper! The moment is yours.',
    options: [
      { key: 'power', label: 'Power shot low corner', risk: 'high', shot: 0.78, assistChance: 0, rating: { success: 1.4, fail: -0.7 } },
      { key: 'chip', label: 'Cheeky chip over keeper', risk: 'high', shot: 0.65, assistChance: 0, rating: { success: 1.6, fail: -0.9 } },
      { key: 'roll', label: 'Roll it past to teammate', risk: 'low', shot: 0, assistChance: 0.75, rating: { success: 1.2, fail: -0.3 } }
    ]
  },
  {
    id: 'penalty_box_cross',
    minute: [20, 75],
    scenario: 'Wide on the flank, defender backing off. Teammate lurks at the far post.',
    options: [
      { key: 'whip', label: 'Whip in an early cross', risk: 'med', shot: 0, assistChance: 0.5, rating: { success: 0.9, fail: -0.25 } },
      { key: 'cut_back', label: 'Drive to the byline, cut back', risk: 'med', shot: 0, assistChance: 0.6, rating: { success: 1.0, fail: -0.35 } },
      { key: 'beat_man', label: 'Take on the full-back', risk: 'high', shot: 0.2, assistChance: 0.35, rating: { success: 1.1, fail: -0.55 } }
    ]
  },
  {
    id: 'volley_chance',
    minute: [30, 85],
    scenario: 'Ball drops invitingly in the box off a header from a teammate.',
    options: [
      { key: 'volley', label: 'First-time volley', risk: 'high', shot: 0.62, assistChance: 0, rating: { success: 1.5, fail: -0.7 } },
      { key: 'cushion', label: 'Cushion it, then shoot', risk: 'med', shot: 0.42, assistChance: 0, rating: { success: 1.0, fail: -0.45 } },
      { key: 'layoff', label: 'Lay it off for teammate', risk: 'low', shot: 0, assistChance: 0.55, rating: { success: 0.85, fail: -0.2 } }
    ]
  },
  {
    id: 'header_chance',
    minute: [25, 85],
    scenario: 'Cross swings into the six-yard box — you rise to meet it.',
    options: [
      { key: 'power_head', label: 'Power header on goal', risk: 'high', shot: 0.58, assistChance: 0, rating: { success: 1.3, fail: -0.5 } },
      { key: 'glance', label: 'Glancing header to far post', risk: 'med', shot: 0.35, assistChance: 0.25, rating: { success: 1.1, fail: -0.35 } },
      { key: 'nod_down', label: 'Nod down to teammate', risk: 'low', shot: 0, assistChance: 0.55, rating: { success: 0.85, fail: -0.2 } }
    ]
  },
  {
    id: 'edge_of_area',
    minute: [18, 80],
    scenario: 'Space opens up 20 yards out — what now?',
    options: [
      { key: 'rocket', label: 'Unleash a rocket', risk: 'high', shot: 0.48, assistChance: 0, rating: { success: 1.35, fail: -0.55 } },
      { key: 'curler', label: 'Curl it top corner', risk: 'high', shot: 0.5, assistChance: 0, rating: { success: 1.5, fail: -0.6 } },
      { key: 'slip', label: 'Slip a through ball', risk: 'med', shot: 0, assistChance: 0.5, rating: { success: 1.0, fail: -0.3 } }
    ]
  },
  {
    id: 'counter_break',
    minute: [25, 80],
    scenario: 'Opposition over-committed — you lead a 3-on-2 counter.',
    options: [
      { key: 'drive', label: 'Drive at the defender alone', risk: 'high', shot: 0.4, assistChance: 0.15, rating: { success: 1.2, fail: -0.6 } },
      { key: 'slide', label: 'Slide the runner in', risk: 'med', shot: 0, assistChance: 0.65, rating: { success: 1.15, fail: -0.3 } },
      { key: 'square', label: 'Square ball to far post', risk: 'low', shot: 0, assistChance: 0.55, rating: { success: 1.0, fail: -0.2 } }
    ]
  },
  {
    id: 'rebound_shot',
    minute: [30, 85],
    scenario: 'Keeper parries a shot — ball spills to your feet at the edge of the six-yard box!',
    options: [
      { key: 'tap_in', label: 'Tap it home first-time', risk: 'low', shot: 0.78, assistChance: 0, rating: { success: 1.3, fail: -0.4 } },
      { key: 'lob', label: 'Lob the sprawling keeper', risk: 'high', shot: 0.55, assistChance: 0, rating: { success: 1.5, fail: -0.7 } },
      { key: 'pull_back', label: 'Pull back to onrushing teammate', risk: 'med', shot: 0, assistChance: 0.7, rating: { success: 1.1, fail: -0.3 } }
    ]
  },
  {
    id: 'solo_run',
    minute: [20, 75],
    scenario: 'You pick up the ball in midfield with acres of space ahead.',
    options: [
      { key: 'mazy', label: 'Go on a mazy solo run', risk: 'high', shot: 0.35, assistChance: 0.2, rating: { success: 1.25, fail: -0.55 } },
      { key: 'pass_advance', label: 'Play a one-two and advance', risk: 'med', shot: 0.2, assistChance: 0.25, rating: { success: 0.95, fail: -0.3 } },
      { key: 'keep', label: 'Keep possession, recycle', risk: 'low', shot: 0, assistChance: 0.1, rating: { success: 0.45, fail: -0.1 } }
    ]
  },

  // ==================== DEFENSIVE SCENARIOS ====================
  {
    id: 'defensive_press',
    minute: [22, 55],
    scenario: 'Opposition is building from the back. The ball carrier is exposed.',
    options: [
      { key: 'press', label: 'Aggressive press', risk: 'high', shot: 0, assistChance: 0.15, rating: { success: 0.9, fail: -0.8 } },
      { key: 'hold_shape', label: 'Hold defensive shape', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.4, fail: -0.1 } },
      { key: 'track_back', label: 'Track back to cover', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.55, fail: -0.15 } }
    ]
  },
  {
    id: 'last_man_tackle',
    minute: [35, 85],
    scenario: 'Striker has you as the last defender — he sprints into the box.',
    options: [
      { key: 'slide', label: 'Slide tackle', risk: 'high', shot: 0, assistChance: 0, rating: { success: 1.1, fail: -1.2 } },
      { key: 'shepherd', label: 'Shepherd him wide', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.8, fail: -0.5 } },
      { key: 'foul', label: 'Take the tactical foul', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.3, fail: -0.6 } }
    ]
  },
  {
    id: 'clearance',
    minute: [15, 85],
    scenario: 'Cross comes in, you are first to the ball under pressure.',
    options: [
      { key: 'boot', label: 'Boot it clear', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.55, fail: -0.25 } },
      { key: 'chest_control', label: 'Chest it down and play out', risk: 'high', shot: 0, assistChance: 0.25, rating: { success: 1.0, fail: -0.8 } },
      { key: 'head_clear', label: 'Powerful header clearance', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.7, fail: -0.2 } }
    ]
  },
  {
    id: 'interception',
    minute: [15, 80],
    scenario: 'Opponent attempts a through ball — you anticipate the route.',
    options: [
      { key: 'intercept', label: 'Step up and intercept', risk: 'med', shot: 0, assistChance: 0.2, rating: { success: 0.9, fail: -0.5 } },
      { key: 'drop_off', label: 'Drop off and cover', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.55, fail: -0.15 } },
      { key: 'man_mark', label: 'Stick tight to the runner', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.7, fail: -0.35 } }
    ]
  },
  {
    id: 'block_shot',
    minute: [25, 85],
    scenario: 'Striker shapes to shoot 18 yards out. Do you commit?',
    options: [
      { key: 'block', label: 'Throw yourself into the block', risk: 'high', shot: 0, assistChance: 0, rating: { success: 1.2, fail: -0.5 } },
      { key: 'close_down', label: 'Close down the angle', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.8, fail: -0.3 } },
      { key: 'hold_line', label: 'Hold line, trust keeper', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.4, fail: -0.2 } }
    ]
  },
  {
    id: 'aerial_duel',
    minute: [15, 85],
    scenario: 'Long ball pumped forward — aerial duel with their striker.',
    options: [
      { key: 'attack_ball', label: 'Attack the ball aggressively', risk: 'med', shot: 0, assistChance: 0.1, rating: { success: 0.9, fail: -0.35 } },
      { key: 'body_position', label: 'Use body to hold him off', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.65, fail: -0.2 } },
      { key: 'let_bounce', label: 'Let it bounce, play it safe', risk: 'high', shot: 0, assistChance: 0, rating: { success: 0.25, fail: -0.7 } }
    ]
  },

  // ==================== MIDFIELD / DISTRIBUTION SCENARIOS ====================
  {
    id: 'midfield_turnover',
    minute: [20, 75],
    scenario: 'You win the ball back in midfield. What is the next move?',
    options: [
      { key: 'quick_forward', label: 'Quick forward pass', risk: 'med', shot: 0, assistChance: 0.35, rating: { success: 0.9, fail: -0.35 } },
      { key: 'drive_forward', label: 'Drive forward with ball', risk: 'med', shot: 0.2, assistChance: 0.2, rating: { success: 0.95, fail: -0.4 } },
      { key: 'recycle', label: 'Recycle to the back', risk: 'low', shot: 0, assistChance: 0.05, rating: { success: 0.4, fail: -0.1 } }
    ]
  },
  {
    id: 'switch_play',
    minute: [15, 75],
    scenario: 'The pitch tilts — space on the opposite flank.',
    options: [
      { key: 'long_diag', label: 'Long diagonal switch', risk: 'med', shot: 0, assistChance: 0.3, rating: { success: 0.95, fail: -0.35 } },
      { key: 'short_combo', label: 'Short combo through middle', risk: 'low', shot: 0, assistChance: 0.15, rating: { success: 0.6, fail: -0.15 } },
      { key: 'dribble_through', label: 'Dribble through traffic', risk: 'high', shot: 0.15, assistChance: 0.2, rating: { success: 1.0, fail: -0.55 } }
    ]
  },
  {
    id: 'deep_playmaking',
    minute: [10, 65],
    scenario: 'Ball drops to you in your own half — full field ahead.',
    options: [
      { key: 'ping', label: 'Ping a 40-yard pass', risk: 'med', shot: 0, assistChance: 0.4, rating: { success: 1.0, fail: -0.35 } },
      { key: 'carry', label: 'Carry the ball forward', risk: 'med', shot: 0, assistChance: 0.2, rating: { success: 0.75, fail: -0.3 } },
      { key: 'simple', label: 'Simple pass to a full-back', risk: 'low', shot: 0, assistChance: 0.08, rating: { success: 0.4, fail: -0.1 } }
    ]
  },
  {
    id: 'tight_space',
    minute: [20, 80],
    scenario: 'Three opponents close you down with your back to goal.',
    options: [
      { key: 'spin', label: 'Spin and take them on', risk: 'high', shot: 0.1, assistChance: 0.2, rating: { success: 1.1, fail: -0.6 } },
      { key: 'one_two', label: 'Quick one-two with teammate', risk: 'med', shot: 0, assistChance: 0.3, rating: { success: 0.9, fail: -0.35 } },
      { key: 'shield', label: 'Shield the ball, wait for help', risk: 'low', shot: 0, assistChance: 0.05, rating: { success: 0.5, fail: -0.15 } }
    ]
  },

  // ==================== SET PIECES ====================
  {
    id: 'set_piece',
    minute: [30, 70],
    scenario: 'Free kick, 22 yards out, slight angle.',
    options: [
      { key: 'direct', label: 'Direct effort on goal', risk: 'high', shot: 0.50, assistChance: 0, rating: { success: 1.3, fail: -0.5 } },
      { key: 'cross', label: 'Whip it into the box', risk: 'med', shot: 0, assistChance: 0.4, rating: { success: 0.9, fail: -0.2 } },
      { key: 'short', label: 'Play it short', risk: 'low', shot: 0, assistChance: 0.15, rating: { success: 0.4, fail: -0.1 } }
    ]
  },
  {
    id: 'corner_kick',
    minute: [15, 85],
    scenario: 'Corner awarded — you step up to take it.',
    options: [
      { key: 'inswinger', label: 'Inswinger to the near post', risk: 'med', shot: 0, assistChance: 0.4, rating: { success: 0.9, fail: -0.25 } },
      { key: 'outswinger', label: 'Outswinger to the far post', risk: 'med', shot: 0, assistChance: 0.45, rating: { success: 0.95, fail: -0.25 } },
      { key: 'short_corner', label: 'Play a short corner', risk: 'low', shot: 0, assistChance: 0.2, rating: { success: 0.5, fail: -0.15 } }
    ]
  },
  {
    id: 'penalty_kick',
    minute: [25, 88],
    scenario: 'Penalty awarded! You grab the ball.',
    options: [
      { key: 'power_pen', label: 'Blast it down the middle', risk: 'high', shot: 0.72, assistChance: 0, rating: { success: 1.4, fail: -0.8 } },
      { key: 'placed_pen', label: 'Side-foot into the corner', risk: 'med', shot: 0.82, assistChance: 0, rating: { success: 1.5, fail: -0.6 } },
      { key: 'panenka', label: 'Audacious Panenka chip', risk: 'high', shot: 0.6, assistChance: 0, rating: { success: 1.9, fail: -1.2 } }
    ]
  },
  {
    id: 'throw_in',
    minute: [10, 85],
    scenario: 'Throw-in deep in opposition half.',
    options: [
      { key: 'long_throw', label: 'Long throw into the box', risk: 'med', shot: 0, assistChance: 0.25, rating: { success: 0.7, fail: -0.2 } },
      { key: 'short_throw', label: 'Quick short throw', risk: 'low', shot: 0, assistChance: 0.15, rating: { success: 0.45, fail: -0.1 } },
      { key: 'trick', label: 'Disguised routine', risk: 'high', shot: 0, assistChance: 0.35, rating: { success: 1.0, fail: -0.4 } }
    ]
  },

  // ==================== PRESSURE / GAME MANAGEMENT ====================
  {
    id: 'late_game',
    minute: [75, 88],
    scenario: 'Final minutes, fatigue is setting in. Space opens up.',
    options: [
      { key: 'burst', label: 'Burst forward, take on defender', risk: 'high', shot: 0.4, assistChance: 0.25, rating: { success: 1.15, fail: -0.6 } },
      { key: 'slow_down', label: 'Slow it down, retain ball', risk: 'low', shot: 0, assistChance: 0.1, rating: { success: 0.5, fail: -0.1 } },
      { key: 'long_ball', label: 'Switch play with long ball', risk: 'med', shot: 0, assistChance: 0.3, rating: { success: 0.75, fail: -0.3 } }
    ]
  },
  {
    id: 'time_wasting',
    minute: [80, 92],
    scenario: "You're leading by one — ball rolls out of play near the corner flag.",
    options: [
      { key: 'wind_down', label: 'Take your time, wind the clock', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.6, fail: -0.1 } },
      { key: 'hold_corner', label: 'Shield at the corner flag', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.7, fail: -0.15 } },
      { key: 'attack_third', label: 'Push for a second goal', risk: 'high', shot: 0.3, assistChance: 0.15, rating: { success: 1.1, fail: -0.55 } }
    ]
  },
  {
    id: 'chasing_game',
    minute: [70, 90],
    scenario: "Down a goal, time running out. Ball at your feet, 35 yards from goal.",
    options: [
      { key: 'hail_mary', label: 'Hail-mary shot from distance', risk: 'high', shot: 0.28, assistChance: 0, rating: { success: 1.6, fail: -0.6 } },
      { key: 'direct_run', label: 'Direct forward run', risk: 'med', shot: 0.18, assistChance: 0.3, rating: { success: 1.0, fail: -0.35 } },
      { key: 'patient', label: 'Patient build-up', risk: 'low', shot: 0, assistChance: 0.2, rating: { success: 0.55, fail: -0.15 } }
    ]
  },
  {
    id: 'tight_game',
    minute: [55, 80],
    scenario: 'Score tied, tempo slows. You sense an opening.',
    options: [
      { key: 'force', label: 'Force the issue, drive forward', risk: 'high', shot: 0.3, assistChance: 0.25, rating: { success: 1.1, fail: -0.5 } },
      { key: 'probe', label: 'Probe patiently, wait for gap', risk: 'med', shot: 0.1, assistChance: 0.2, rating: { success: 0.75, fail: -0.2 } },
      { key: 'safety', label: 'Keep it safe, no mistakes', risk: 'low', shot: 0, assistChance: 0.08, rating: { success: 0.4, fail: -0.1 } }
    ]
  },

  // ==================== GOALKEEPER SPECIFIC (useful if GK career) ====================
  {
    id: 'gk_distribution',
    minute: [10, 80],
    scenario: 'Ball comes back to you — do you launch it or play out?',
    options: [
      { key: 'long_kick', label: 'Launch it long', risk: 'med', shot: 0, assistChance: 0.25, rating: { success: 0.65, fail: -0.25 } },
      { key: 'roll_short', label: 'Roll it short to a defender', risk: 'low', shot: 0, assistChance: 0.1, rating: { success: 0.55, fail: -0.15 } },
      { key: 'drop_kick', label: 'Drop kick into channel', risk: 'med', shot: 0, assistChance: 0.2, rating: { success: 0.7, fail: -0.25 } }
    ]
  },
  {
    id: 'gk_claim',
    minute: [15, 85],
    scenario: 'Cross whipped into your six-yard box.',
    options: [
      { key: 'claim_cross', label: 'Come and claim it', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.95, fail: -0.6 } },
      { key: 'punch_clear', label: 'Punch it clear', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.8, fail: -0.35 } },
      { key: 'stay_line', label: 'Stay on your line', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.45, fail: -0.2 } }
    ]
  },

  // ==================== BEHAVIOURAL / GAME INTELLIGENCE ====================
  {
    id: 'caught_in_possession',
    minute: [20, 80],
    scenario: 'Opponent swarms you in a dangerous area of the pitch.',
    options: [
      { key: 'flick', label: 'Cheeky flick to escape', risk: 'high', shot: 0, assistChance: 0.25, rating: { success: 1.1, fail: -0.6 } },
      { key: 'back_pass', label: 'Pass back to safety', risk: 'low', shot: 0, assistChance: 0.05, rating: { success: 0.4, fail: -0.2 } },
      { key: 'win_foul', label: 'Shield the ball and win a foul', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.7, fail: -0.35 } }
    ]
  },
  {
    id: 'injured_teammate',
    minute: [30, 85],
    scenario: 'Teammate down injured — ball still in play in a dangerous area.',
    options: [
      { key: 'play_on', label: 'Play on, exploit the advantage', risk: 'high', shot: 0.3, assistChance: 0.25, rating: { success: 0.9, fail: -0.4 } },
      { key: 'kick_out', label: 'Kick it out for treatment', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.7, fail: -0.1 } },
      { key: 'control', label: 'Keep possession, slow it', risk: 'med', shot: 0, assistChance: 0.1, rating: { success: 0.5, fail: -0.2 } }
    ]
  },
  {
    id: 'referee_decision',
    minute: [25, 80],
    scenario: 'Controversial call by the referee against you.',
    options: [
      { key: 'protest', label: 'Protest strongly', risk: 'high', shot: 0, assistChance: 0, rating: { success: 0.1, fail: -0.8 } },
      { key: 'focus', label: 'Stay focused on next play', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.7, fail: -0.1 } },
      { key: 'word_captain', label: 'Have a word through the captain', risk: 'med', shot: 0, assistChance: 0, rating: { success: 0.5, fail: -0.3 } }
    ]
  },
  {
    id: 'leadership_moment',
    minute: [40, 85],
    scenario: 'Team conceded — heads dropping around you.',
    options: [
      { key: 'rally', label: 'Rally the team with a shout', risk: 'low', shot: 0, assistChance: 0, rating: { success: 0.75, fail: -0.2 } },
      { key: 'lead_by_example', label: 'Lead by example — big moment', risk: 'high', shot: 0.25, assistChance: 0.3, rating: { success: 1.3, fail: -0.5 } },
      { key: 'calm_down', label: 'Calm everyone down', risk: 'low', shot: 0, assistChance: 0.08, rating: { success: 0.6, fail: -0.15 } }
    ]
  }
];

// Pick 3 decisions spread across the match
export const generateMatchDecisions = () => {
  // Randomize 3 templates, sorted by minute
  const shuffled = [...DECISION_TEMPLATES].sort(() => Math.random() - 0.5).slice(0, 3);
  return shuffled.map(d => {
    const [min, max] = d.minute;
    return { ...d, atMinute: Math.floor(Math.random() * (max - min + 1)) + min };
  }).sort((a, b) => a.atMinute - b.atMinute);
};

// Resolve a decision with success/fail based on player overall + option probabilities
export const resolveDecision = (player, option) => {
  const baseSuccess = 0.35 + ((player.overall || 70) - 70) * 0.012; // 70 OVR ≈ 35%, 90 OVR ≈ 60%
  const riskModifier = option.risk === 'high' ? -0.1 : option.risk === 'med' ? 0 : 0.1;
  const successProb = Math.max(0.18, Math.min(0.88, baseSuccess + riskModifier));
  const success = Math.random() < successProb;
  return { success, successProb };
};

// Apply decision outcomes: goal, assist, rating boost/penalty
export const applyDecisionOutcome = (matchState, player, team, opponent, option, decisionResult) => {
  const events = [];
  const nextState = { ...matchState };
  nextState.playerEvents = [...(nextState.playerEvents || [])];
  const minute = nextState.currentDecisionMinute || 45;
  const { success } = decisionResult;

  // Rating impact
  const ratingDelta = success ? option.rating.success : option.rating.fail;
  nextState.playerRatingDelta = (nextState.playerRatingDelta || 0) + ratingDelta;

  // Goal chance
  if (success && option.shot > 0) {
    const goalChance = option.shot * (0.6 + ((player.overall || 70) - 70) * 0.015);
    if (Math.random() < goalChance) {
      nextState.playerGoals = (nextState.playerGoals || 0) + 1;
      nextState.homeScore = team.isHome ? nextState.homeScore + 1 : nextState.homeScore;
      nextState.awayScore = !team.isHome ? nextState.awayScore + 1 : nextState.awayScore;
      events.push({ minute, type: 'goal', scorer: player.name, team: team.isHome ? 'home' : 'away', teamName: team.name, commentary: `GOAL! ${player.name} finishes it off after your decision paid off!` });
      nextState.playerEvents.push({ minute, type: 'goal', commentary: `You scored! ${option.label}` });
    }
  }
  // Assist chance
  if (success && option.assistChance > 0) {
    const assistChance = option.assistChance * (0.5 + ((player.overall || 70) - 70) * 0.012);
    if (Math.random() < assistChance) {
      nextState.playerAssists = (nextState.playerAssists || 0) + 1;
      nextState.homeScore = team.isHome ? nextState.homeScore + 1 : nextState.homeScore;
      nextState.awayScore = !team.isHome ? nextState.awayScore + 1 : nextState.awayScore;
      events.push({ minute, type: 'goal', scorer: 'Teammate', assister: player.name, team: team.isHome ? 'home' : 'away', teamName: team.name, commentary: `Assist by ${player.name}!` });
      nextState.playerEvents.push({ minute, type: 'assist', commentary: `Your ${option.label.toLowerCase()} created a goal!` });
    }
  }
  if (!success) {
    nextState.playerEvents.push({ minute, type: 'missed', commentary: `Your ${option.label.toLowerCase()} didn't come off.` });
  } else if (!nextState.playerEvents.some(e => e.minute === minute)) {
    nextState.playerEvents.push({ minute, type: 'positive', commentary: `${option.label} — successful play.` });
  }
  nextState.matchEvents = [...(nextState.matchEvents || []), ...events];
  return nextState;
};

// Compute final player rating (5.5 - 10.0 scale) from decisions + baseline + goals/assists
export const computePlayerRating = (player, matchState, opponentOvr, teamOvr) => {
  let base = 6.0;
  // Baseline influenced by team strength vs opponent
  base += (teamOvr - opponentOvr) * 0.02;
  // Apply decision rating deltas
  base += matchState.playerRatingDelta || 0;
  // Goals & assists
  base += (matchState.playerGoals || 0) * 1.0;
  base += (matchState.playerAssists || 0) * 0.65;
  // Random variance
  base += (Math.random() - 0.5) * 0.4;
  return Math.round(Math.max(4.0, Math.min(10.0, base)) * 10) / 10;
};

// Simulate backgrounds events (other matches) on same week
export const simulateOtherMatches = (teams, fixtures, weekNumber, playerTeamId) => {
  const weekFixtures = fixtures.filter(f => f.week === weekNumber && !f.played && f.homeTeamId !== playerTeamId && f.awayTeamId !== playerTeamId);
  let updatedTeams = [...teams];
  let updatedFixtures = [...fixtures];
  weekFixtures.forEach(fixture => {
    const home = updatedTeams.find(t => t.id === fixture.homeTeamId);
    const away = updatedTeams.find(t => t.id === fixture.awayTeamId);
    if (!home || !away) return;
    const result = quickSimulate(home, away);
    const fi = updatedFixtures.findIndex(f => f.id === fixture.id);
    updatedFixtures[fi] = { ...fixture, homeScore: result.homeScore, awayScore: result.awayScore, played: true, events: result.events, matchRatings: result.matchRatings };
    const isHomeWin = result.homeScore > result.awayScore;
    const isDraw = result.homeScore === result.awayScore;
    const hi = updatedTeams.findIndex(t => t.id === home.id);
    const ai = updatedTeams.findIndex(t => t.id === away.id);
    updatedTeams[hi] = updateTeamStats(home, result.homeScore, result.awayScore, isHomeWin, isDraw);
    updatedTeams[ai] = updateTeamStats(away, result.awayScore, result.homeScore, !isHomeWin && !isDraw, isDraw);
    updatedTeams = updatePlayerStats(updatedTeams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'league');
    updatedTeams = applyMatchGrowth(updatedTeams, result.matchRatings);
  });
  return { teams: updatedTeams, fixtures: updatedFixtures };
};

// Apply player's match result (goals, assists, rating) to the career state
export const applyPlayerMatchResult = (careerState, matchResult, fixture, homeTeam, awayTeam) => {
  const { playerId, playerTeamId } = careerState;
  const teams = careerState.teams.map(t => ({ ...t }));
  const fixtures = careerState.fixtures.map(f => ({ ...f }));
  const competition = matchResult.competition || 'league';

  // 1. Apply player stats to the player record
  const playerTeam = teams.find(t => t.id === playerTeamId);
  const playerIdx = playerTeam.players.findIndex(p => p.id === playerId);
  if (playerIdx >= 0) {
    const p = { ...playerTeam.players[playerIdx] };
    p.stats = p.stats || EMPTY_STATS();
    p.stats[competition] = { ...(p.stats[competition] || EMPTY_COMP_STATS()) };
    p.stats[competition].matchesPlayed = (p.stats[competition].matchesPlayed || 0) + 1;
    p.stats[competition].goals = (p.stats[competition].goals || 0) + (matchResult.playerGoals || 0);
    p.stats[competition].assists = (p.stats[competition].assists || 0) + (matchResult.playerAssists || 0);
    // Clean sheets: GK only, when the player's team conceded 0
    const playerTeamConceded = homeTeam.id === playerTeamId ? matchResult.awayScore : matchResult.homeScore;
    if (p.position === 'GK' && playerTeamConceded === 0) {
      p.stats[competition].cleanSheets = (p.stats[competition].cleanSheets || 0) + 1;
    }
    // Growth from rating — SLOWER progression
    const rating = matchResult.playerRating || 6.5;
    // Require consistently high ratings; 1 OVR per ~6 strong matches (7.5+)
    const delta = (rating - 6.8) * 0.12;
    const gp = (p.growthPoints || 0) + delta;
    let overall = p.overall || 70;
    let gpRem = gp;
    while (gpRem >= 1 && overall < (p.potential || 99)) { overall += 1; gpRem -= 1; }
    while (gpRem <= -3 && overall > 40) { overall -= 1; gpRem += 3; }
    p.overall = overall;
    p.growthPoints = gpRem;
    playerTeam.players[playerIdx] = p;
  }

  // 2. Apply team stats to both teams (league stats only — bonus comps don't count)
  const isHomeWin = matchResult.homeScore > matchResult.awayScore;
  const isDraw = matchResult.homeScore === matchResult.awayScore;
  if (competition === 'league') {
    const hi = teams.findIndex(t => t.id === homeTeam.id);
    const ai = teams.findIndex(t => t.id === awayTeam.id);
    teams[hi] = updateTeamStats(teams[hi], matchResult.homeScore, matchResult.awayScore, isHomeWin, isDraw);
    teams[ai] = updateTeamStats(teams[ai], matchResult.awayScore, matchResult.homeScore, !isHomeWin && !isDraw, isDraw);
  }

  // 3. Mark fixture played (league fixture table OR bonus fixtures)
  let updatedCompetitions = careerState.competitions;
  if (competition === 'league') {
    const fi = fixtures.findIndex(f => f.id === fixture.id);
    if (fi >= 0) fixtures[fi] = { ...fixture, homeScore: matchResult.homeScore, awayScore: matchResult.awayScore, played: true, events: matchResult.matchEvents || [] };
  } else {
    // Update bonus fixture + progression
    const comps = { ...(careerState.competitions || {}) };
    const bucketMap = { cup: 'cup', championsLeague: 'ucl', europaLeague: 'uel', conferenceLeague: 'uecl', cwc: 'cwc', worldCup: 'wc' };
    const bucket = bucketMap[competition];
    if (bucket && comps[bucket]?.fixtures) {
      comps[bucket] = { ...comps[bucket], fixtures: comps[bucket].fixtures.map(f => f.id === fixture.id ? { ...f, homeScore: matchResult.homeScore, awayScore: matchResult.awayScore, played: true, events: matchResult.matchEvents || [] } : f) };
    }
    // Compute player-team won the bonus match
    const playerTeamIsHome = homeTeam.id === playerTeamId;
    const playerTeamGoals = playerTeamIsHome ? matchResult.homeScore : matchResult.awayScore;
    const opponentGoals = playerTeamIsHome ? matchResult.awayScore : matchResult.homeScore;
    const playerWon = playerTeamGoals > opponentGoals;
    updatedCompetitions = updateCompetitionProgression({ ...careerState, competitions: comps }, { ...fixture, homeScore: matchResult.homeScore, awayScore: matchResult.awayScore }, playerWon);
  }

  // 4. Append to player match history
  const matchHistory = [...(careerState.matchHistory || []), {
    week: fixture.week,
    season: careerState.season || 1,
    competition,
    round: fixture.round,
    homeTeamId: homeTeam.id,
    awayTeamId: awayTeam.id,
    homeScore: matchResult.homeScore,
    awayScore: matchResult.awayScore,
    playerGoals: matchResult.playerGoals || 0,
    playerAssists: matchResult.playerAssists || 0,
    playerRating: matchResult.playerRating || 6.5,
    decisions: matchResult.decisionsMade || []
  }];

  return { ...careerState, teams, fixtures, matchHistory, competitions: updatedCompetitions };
};

// ============================================================
// BONUS COMPETITION FIXTURES — Cup / Champions League / CWC / WC
// Simple bracket: if player's team wins each round, next round is scheduled.
// ============================================================
const CUP_ROUND_WEEKS = [5, 11, 17, 23, 29]; // 5 rounds, knockout
const CUP_ROUND_NAMES = ['R32', 'R16', 'QF', 'SF', 'FINAL'];
// Champions League: 3 group opponents (4 fixtures), then R16/QF/SF
const UCL_GROUP_WEEKS = [7, 13, 19, 25];
const UCL_KO_WEEKS = [31, 34, 37];
const UCL_KO_NAMES = ['R16', 'QF', 'SF'];
// Europa League: 3 group opponents (3 group games) then R16 → QF → SF → FINAL
const UEL_GROUP_WEEKS = [4, 12, 20];
const UEL_KO_WEEKS = [27, 31, 34, 37];
const UEL_KO_NAMES = ['R16', 'QF', 'SF', 'FINAL'];
// Conference League: 3 group opponents then R16 → QF → SF → FINAL
const UECL_GROUP_WEEKS = [3, 14, 22];
const UECL_KO_WEEKS = [28, 32, 35, 38];
const UECL_KO_NAMES = ['R16', 'QF', 'SF', 'FINAL'];

// Legacy alias kept for backwards compatibility — pull random opponent from EWFL teams
const pickRandomOpponent = (teams, excludeId) => pickFromPool(teams, [excludeId]);

// Helper — pick a random opponent from a pool, optionally excluding ids
const pickFromPool = (pool, excludeIds = []) => {
  const candidates = pool.filter(t => !excludeIds.includes(t.id));
  if (!candidates.length) return null;
  return candidates[Math.floor(Math.random() * candidates.length)];
};

// Get the opponent pool for a given competition
const getCompPool = (comps, careerTeams, competition) => {
  if (competition === 'championsLeague') return comps.uclTeams || careerTeams;
  if (competition === 'europaLeague') return comps.uelTeams || careerTeams;
  if (competition === 'conferenceLeague') return comps.ueclTeams || careerTeams;
  if (competition === 'cwc') return comps.cwcTeams || careerTeams;
  return careerTeams; // cup, league
};

// Fixed first-season EWFL qualifications (deterministic for every save).
// These map to the EWFL city+nickname pairings in gameData.js (indices 5/10/11/16, 2/7, 14/18).
const FIRST_SEASON_UCL_TEAMS = [
  'Stormgate Albion',
  'Greycastle Crusaders',
  'Rosendale Hotspur',
  'Wolverstone Argyle',
];
const FIRST_SEASON_UEL_TEAMS = [
  'Ravenshire Town',
  'Blackmoor Rovers',
];
const FIRST_SEASON_UECL_TEAMS = [
  'Brightstone Royals',
  'Millbrook North End',
];

// Build initial cup + UCL fixtures for player's team. CWC/WC are seeded at season boundaries.
export const buildCareerCompetitions = (teams, playerTeamId, seasonRank = null) => {
  // Build international team rosters once (these persist for the whole career)
  const uclTeams = buildChampionsLeagueTeams();
  const uelTeams = buildEuropaLeagueTeams();
  const ueclTeams = buildConferenceLeagueTeams();
  const cwcTeams = buildCWCTeams();

  const playerTeam = teams.find(t => t.id === playerTeamId);
  const playerTeamName = playerTeam?.name || '';

  // First season: hard-coded EWFL qualifiers. After season 1: based on previous season's league rank.
  const isFirstSeason = seasonRank === null;
  const qualifiesUCL  = isFirstSeason ? FIRST_SEASON_UCL_TEAMS.includes(playerTeamName)  : (seasonRank >= 1 && seasonRank <= 4);
  const qualifiesUEL  = isFirstSeason ? FIRST_SEASON_UEL_TEAMS.includes(playerTeamName)  : (seasonRank === 5 || seasonRank === 6);
  const qualifiesUECL = isFirstSeason ? FIRST_SEASON_UECL_TEAMS.includes(playerTeamName) : (seasonRank === 7 || seasonRank === 8);
  const qualifiesCWC  = isFirstSeason ? false : (seasonRank === 1 || seasonRank === 2);

  const comps = {
    // Persistent international rosters used to schedule opponents
    uclTeams,
    uelTeams,
    ueclTeams,
    cwcTeams,
    cup: {
      eliminated: false,
      currentRoundIdx: 0,
      fixtures: []
    },
    ucl: {
      qualified: qualifiesUCL,
      eliminated: false,
      groupPlayed: 0,
      groupPoints: 0,
      groupGF: 0,
      groupGA: 0,
      currentKoIdx: 0,
      group: [],
      fixtures: []
    },
    uel: {
      qualified: qualifiesUEL,
      eliminated: false,
      groupPlayed: 0,
      groupPoints: 0,
      groupGF: 0,
      groupGA: 0,
      currentKoIdx: 0,
      group: [],
      fixtures: []
    },
    uecl: {
      qualified: qualifiesUECL,
      eliminated: false,
      groupPlayed: 0,
      groupPoints: 0,
      groupGF: 0,
      groupGA: 0,
      currentKoIdx: 0,
      group: [],
      fixtures: []
    },
    cwc: {
      qualified: qualifiesCWC,
      eliminated: false,
      groupPlayed: 0,
      groupPoints: 0,
      groupGF: 0,
      groupGA: 0,
      currentKoIdx: 0,
      group: [],
      fixtures: []
    },
    wc: {
      qualified: false,
      fixtures: []
    }
  };
  // Cup R1 fixture
  const r1Opp = pickFromPool(teams, [playerTeamId]);
  if (r1Opp) {
    comps.cup.fixtures.push({
      id: `cup-r0-${Date.now()}`,
      week: CUP_ROUND_WEEKS[0],
      homeTeamId: Math.random() < 0.5 ? playerTeamId : r1Opp.id,
      awayTeamId: Math.random() < 0.5 ? r1Opp.id : playerTeamId,
      round: CUP_ROUND_NAMES[0],
      competition: 'cup',
      played: false
    });
  }
  // UCL group stage: 4 matches against 3 unique pool opponents (rotated home/away once each = 4 fixtures with one team played twice for variety)
  if (comps.ucl.qualified) {
    const groupOpps = [];
    const usedIds = [playerTeamId];
    for (let i = 0; i < 3; i++) {
      const o = pickFromPool(uclTeams, usedIds);
      if (o) { groupOpps.push(o); usedIds.push(o.id); }
    }
    // Initialize the group standings shadow (player + 3 opponents, each will play 4 matches in real scheduling)
    comps.ucl.group = [
      { teamId: playerTeamId, name: playerTeam?.name || 'You', played: 0, pts: 0, gf: 0, ga: 0, isPlayer: true },
      ...groupOpps.map(o => ({ teamId: o.id, name: o.name, played: 0, pts: 0, gf: 0, ga: 0, isPlayer: false }))
    ];
    // Schedule player's 4 group fixtures — opponents cycle (3 unique, last week repeats first)
    UCL_GROUP_WEEKS.forEach((week, idx) => {
      const opp = groupOpps[idx % groupOpps.length] || groupOpps[0];
      comps.ucl.fixtures.push({
        id: `ucl-g${idx}-${Date.now()}-${idx}`,
        week,
        homeTeamId: idx % 2 === 0 ? playerTeamId : opp.id,
        awayTeamId: idx % 2 === 0 ? opp.id : playerTeamId,
        round: `GROUP ${idx + 1}`,
        competition: 'championsLeague',
        played: false,
        stage: 'group'
      });
    });
  }
  // UEL group stage: 3 group opponents over 3 fixtures, then R16/QF/SF/FINAL
  if (comps.uel.qualified) {
    const opps = [];
    const usedIds = [playerTeamId];
    for (let i = 0; i < 3; i++) {
      const o = pickFromPool(uelTeams, usedIds);
      if (o) { opps.push(o); usedIds.push(o.id); }
    }
    comps.uel.group = [
      { teamId: playerTeamId, name: playerTeam?.name || 'You', played: 0, pts: 0, gf: 0, ga: 0, isPlayer: true },
      ...opps.map(o => ({ teamId: o.id, name: o.name, played: 0, pts: 0, gf: 0, ga: 0, isPlayer: false }))
    ];
    UEL_GROUP_WEEKS.forEach((week, idx) => {
      const opp = opps[idx % opps.length] || opps[0];
      if (!opp) return;
      comps.uel.fixtures.push({
        id: `uel-g${idx}-${Date.now()}-${idx}`,
        week,
        homeTeamId: idx % 2 === 0 ? playerTeamId : opp.id,
        awayTeamId: idx % 2 === 0 ? opp.id : playerTeamId,
        round: `GROUP ${idx + 1}`,
        competition: 'europaLeague',
        played: false,
        stage: 'group'
      });
    });
  }
  // UECL group stage — same shape as UEL
  if (comps.uecl.qualified) {
    const opps = [];
    const usedIds = [playerTeamId];
    for (let i = 0; i < 3; i++) {
      const o = pickFromPool(ueclTeams, usedIds);
      if (o) { opps.push(o); usedIds.push(o.id); }
    }
    comps.uecl.group = [
      { teamId: playerTeamId, name: playerTeam?.name || 'You', played: 0, pts: 0, gf: 0, ga: 0, isPlayer: true },
      ...opps.map(o => ({ teamId: o.id, name: o.name, played: 0, pts: 0, gf: 0, ga: 0, isPlayer: false }))
    ];
    UECL_GROUP_WEEKS.forEach((week, idx) => {
      const opp = opps[idx % opps.length] || opps[0];
      if (!opp) return;
      comps.uecl.fixtures.push({
        id: `uecl-g${idx}-${Date.now()}-${idx}`,
        week,
        homeTeamId: idx % 2 === 0 ? playerTeamId : opp.id,
        awayTeamId: idx % 2 === 0 ? opp.id : playerTeamId,
        round: `GROUP ${idx + 1}`,
        competition: 'conferenceLeague',
        played: false,
        stage: 'group'
      });
    });
  }
  // CWC group stage (top 2 league next season) — 3 matches vs 3 CWC pool teams
  if (comps.cwc.qualified) {
    const opps = [];
    const usedIds = [playerTeamId];
    for (let i = 0; i < 3; i++) {
      const o = pickFromPool(cwcTeams, usedIds);
      if (o) { opps.push(o); usedIds.push(o.id); }
    }
    comps.cwc.group = [
      { teamId: playerTeamId, name: playerTeam?.name || 'You', played: 0, pts: 0, gf: 0, ga: 0, isPlayer: true },
      ...opps.map(o => ({ teamId: o.id, name: o.name, played: 0, pts: 0, gf: 0, ga: 0, isPlayer: false }))
    ];
    // Player plays 3 CWC group games — use UCL group weeks +1 to space them
    [UCL_GROUP_WEEKS[0] + 8, UCL_GROUP_WEEKS[1] + 8, UCL_GROUP_WEEKS[2] + 8].forEach((week, idx) => {
      const opp = opps[idx];
      if (!opp) return;
      comps.cwc.fixtures.push({
        id: `cwc-g${idx}-${Date.now()}-${idx}`,
        week,
        homeTeamId: idx % 2 === 0 ? playerTeamId : opp.id,
        awayTeamId: idx % 2 === 0 ? opp.id : playerTeamId,
        round: `CWC GROUP ${idx + 1}`,
        competition: 'cwc',
        played: false,
        stage: 'group'
      });
    });
  }
  return comps;
};

// Legacy alias kept for backwards compatibility — pull random opponent from EWFL teams (cup/league only)
const _pickEwflOpponent = (teams, excludeId) => pickFromPool(teams, [excludeId]);

// After the player's cup/ucl match, decide if they advance and schedule next round
export const updateCompetitionProgression = (careerState, fixture, playerWon) => {
  const comps = { ...(careerState.competitions || {}) };
  const comp = fixture.competition;

  // For cup, opponents come from the EWFL pool (domestic cup)
  // For UCL/UEL/UECL/CWC, opponents come from the persistent international pools stored in comps
  const oppPool = getCompPool(comps, careerState.teams, comp);
  const usedIds = [careerState.playerTeamId, fixture.homeTeamId, fixture.awayTeamId];

  if (comp === 'cup') {
    const cup = { ...comps.cup };
    if (!playerWon) {
      cup.eliminated = true;
    } else {
      cup.currentRoundIdx = (cup.currentRoundIdx || 0) + 1;
      if (cup.currentRoundIdx < CUP_ROUND_WEEKS.length) {
        const nextOpp = _pickEwflOpponent(careerState.teams, careerState.playerTeamId);
        cup.fixtures.push({
          id: `cup-r${cup.currentRoundIdx}-${Date.now()}`,
          week: CUP_ROUND_WEEKS[cup.currentRoundIdx],
          homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
          awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
          round: CUP_ROUND_NAMES[cup.currentRoundIdx],
          competition: 'cup',
          played: false
        });
      } else {
        cup.won = true;
      }
    }
    comps.cup = cup;
  } else if (comp === 'championsLeague') {
    const ucl = { ...comps.ucl };
    // Update the player's group row
    const grp = (ucl.group || []).map(r => ({ ...r }));
    const playerIsHome = fixture.homeTeamId === careerState.playerTeamId;
    const playerScore = playerIsHome ? fixture.homeScore : fixture.awayScore;
    const oppScore = playerIsHome ? fixture.awayScore : fixture.homeScore;
    const playerRow = grp.find(r => r.teamId === careerState.playerTeamId);
    if (playerRow) {
      playerRow.played += 1;
      playerRow.gf += playerScore || 0;
      playerRow.ga += oppScore || 0;
      playerRow.pts += playerWon ? 3 : (playerScore === oppScore ? 1 : 0);
    }
    // Auto-sim the OTHER 2 opponents' match in this matchday for realism
    const otherOpps = grp.filter(r => !r.isPlayer && r.teamId !== fixture.homeTeamId && r.teamId !== fixture.awayTeamId);
    if (otherOpps.length >= 2) {
      const a = otherOpps[0], b = otherOpps[1];
      const aGoals = Math.floor(Math.random() * 4);
      const bGoals = Math.floor(Math.random() * 4);
      a.played += 1; b.played += 1;
      a.gf += aGoals; a.ga += bGoals;
      b.gf += bGoals; b.ga += aGoals;
      if (aGoals > bGoals) a.pts += 3;
      else if (bGoals > aGoals) b.pts += 3;
      else { a.pts += 1; b.pts += 1; }
    }
    ucl.group = grp;

    if (fixture.stage === 'group') {
      ucl.groupPlayed = (ucl.groupPlayed || 0) + 1;
      if (playerWon) ucl.groupPoints += 3;
      else if (fixture.homeScore === fixture.awayScore) ucl.groupPoints += 1;
      ucl.groupGF = (ucl.groupGF || 0) + (playerScore || 0);
      ucl.groupGA = (ucl.groupGA || 0) + (oppScore || 0);
      // When all 4 group games played, top 2 (6+ points) qualify for KO
      if (ucl.groupPlayed >= UCL_GROUP_WEEKS.length) {
        if (ucl.groupPoints >= 5) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            ucl.fixtures.push({
              id: `ucl-ko0-${Date.now()}`,
              week: UCL_KO_WEEKS[0],
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: UCL_KO_NAMES[0],
              competition: 'championsLeague',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          ucl.eliminated = true;
        }
      }
    } else if (fixture.stage === 'ko') {
      if (!playerWon) {
        ucl.eliminated = true;
      } else {
        ucl.currentKoIdx = (ucl.currentKoIdx || 0) + 1;
        if (ucl.currentKoIdx < UCL_KO_WEEKS.length) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            ucl.fixtures.push({
              id: `ucl-ko${ucl.currentKoIdx}-${Date.now()}`,
              week: UCL_KO_WEEKS[ucl.currentKoIdx],
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: UCL_KO_NAMES[ucl.currentKoIdx] || 'FINAL',
              competition: 'championsLeague',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          ucl.won = true;
        }
      }
    }
    comps.ucl = ucl;
  } else if (comp === 'europaLeague') {
    const uel = { ...comps.uel };
    // Update player's group row
    const grp = (uel.group || []).map(r => ({ ...r }));
    const playerIsHome = fixture.homeTeamId === careerState.playerTeamId;
    const playerScore = playerIsHome ? fixture.homeScore : fixture.awayScore;
    const oppScore = playerIsHome ? fixture.awayScore : fixture.homeScore;
    const playerRow = grp.find(r => r.teamId === careerState.playerTeamId);
    if (playerRow) {
      playerRow.played += 1;
      playerRow.gf += playerScore || 0;
      playerRow.ga += oppScore || 0;
      playerRow.pts += playerWon ? 3 : (playerScore === oppScore ? 1 : 0);
    }
    // Auto-sim the OTHER 2 group teams' clash for live standings realism
    const otherOpps = grp.filter(r => !r.isPlayer && r.teamId !== fixture.homeTeamId && r.teamId !== fixture.awayTeamId);
    if (otherOpps.length >= 2) {
      const a = otherOpps[0], b = otherOpps[1];
      const aGoals = Math.floor(Math.random() * 4);
      const bGoals = Math.floor(Math.random() * 4);
      a.played += 1; b.played += 1;
      a.gf += aGoals; a.ga += bGoals;
      b.gf += bGoals; b.ga += aGoals;
      if (aGoals > bGoals) a.pts += 3;
      else if (bGoals > aGoals) b.pts += 3;
      else { a.pts += 1; b.pts += 1; }
    }
    uel.group = grp;

    if (fixture.stage === 'group') {
      uel.groupPlayed = (uel.groupPlayed || 0) + 1;
      if (playerWon) uel.groupPoints += 3;
      else if (fixture.homeScore === fixture.awayScore) uel.groupPoints += 1;
      uel.groupGF = (uel.groupGF || 0) + (playerScore || 0);
      uel.groupGA = (uel.groupGA || 0) + (oppScore || 0);
      // After 3 group games, top 2 (4+ points) advance to R16 KO
      if (uel.groupPlayed >= UEL_GROUP_WEEKS.length) {
        if (uel.groupPoints >= 4) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            uel.fixtures.push({
              id: `uel-ko0-${Date.now()}`,
              week: UEL_KO_WEEKS[0],
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: UEL_KO_NAMES[0],
              competition: 'europaLeague',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          uel.eliminated = true;
        }
      }
    } else if (fixture.stage === 'ko') {
      if (!playerWon) {
        uel.eliminated = true;
      } else {
        uel.currentKoIdx = (uel.currentKoIdx || 0) + 1;
        if (uel.currentKoIdx < UEL_KO_WEEKS.length) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            uel.fixtures.push({
              id: `uel-ko${uel.currentKoIdx}-${Date.now()}`,
              week: UEL_KO_WEEKS[uel.currentKoIdx],
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: UEL_KO_NAMES[uel.currentKoIdx] || 'FINAL',
              competition: 'europaLeague',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          uel.won = true;
        }
      }
    }
    comps.uel = uel;
  } else if (comp === 'conferenceLeague') {
    const uecl = { ...comps.uecl };
    const grp = (uecl.group || []).map(r => ({ ...r }));
    const playerIsHome = fixture.homeTeamId === careerState.playerTeamId;
    const playerScore = playerIsHome ? fixture.homeScore : fixture.awayScore;
    const oppScore = playerIsHome ? fixture.awayScore : fixture.homeScore;
    const playerRow = grp.find(r => r.teamId === careerState.playerTeamId);
    if (playerRow) {
      playerRow.played += 1;
      playerRow.gf += playerScore || 0;
      playerRow.ga += oppScore || 0;
      playerRow.pts += playerWon ? 3 : (playerScore === oppScore ? 1 : 0);
    }
    const otherOpps = grp.filter(r => !r.isPlayer && r.teamId !== fixture.homeTeamId && r.teamId !== fixture.awayTeamId);
    if (otherOpps.length >= 2) {
      const a = otherOpps[0], b = otherOpps[1];
      const aGoals = Math.floor(Math.random() * 4);
      const bGoals = Math.floor(Math.random() * 4);
      a.played += 1; b.played += 1;
      a.gf += aGoals; a.ga += bGoals;
      b.gf += bGoals; b.ga += aGoals;
      if (aGoals > bGoals) a.pts += 3;
      else if (bGoals > aGoals) b.pts += 3;
      else { a.pts += 1; b.pts += 1; }
    }
    uecl.group = grp;

    if (fixture.stage === 'group') {
      uecl.groupPlayed = (uecl.groupPlayed || 0) + 1;
      if (playerWon) uecl.groupPoints += 3;
      else if (fixture.homeScore === fixture.awayScore) uecl.groupPoints += 1;
      uecl.groupGF = (uecl.groupGF || 0) + (playerScore || 0);
      uecl.groupGA = (uecl.groupGA || 0) + (oppScore || 0);
      if (uecl.groupPlayed >= UECL_GROUP_WEEKS.length) {
        if (uecl.groupPoints >= 4) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            uecl.fixtures.push({
              id: `uecl-ko0-${Date.now()}`,
              week: UECL_KO_WEEKS[0],
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: UECL_KO_NAMES[0],
              competition: 'conferenceLeague',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          uecl.eliminated = true;
        }
      }
    } else if (fixture.stage === 'ko') {
      if (!playerWon) {
        uecl.eliminated = true;
      } else {
        uecl.currentKoIdx = (uecl.currentKoIdx || 0) + 1;
        if (uecl.currentKoIdx < UECL_KO_WEEKS.length) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            uecl.fixtures.push({
              id: `uecl-ko${uecl.currentKoIdx}-${Date.now()}`,
              week: UECL_KO_WEEKS[uecl.currentKoIdx],
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: UECL_KO_NAMES[uecl.currentKoIdx] || 'FINAL',
              competition: 'conferenceLeague',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          uecl.won = true;
        }
      }
    }
    comps.uecl = uecl;
  } else if (comp === 'cwc') {
    const cwc = { ...comps.cwc };
    const grp = (cwc.group || []).map(r => ({ ...r }));
    const playerIsHome = fixture.homeTeamId === careerState.playerTeamId;
    const playerScore = playerIsHome ? fixture.homeScore : fixture.awayScore;
    const oppScore = playerIsHome ? fixture.awayScore : fixture.homeScore;
    const playerRow = grp.find(r => r.teamId === careerState.playerTeamId);
    if (playerRow) {
      playerRow.played += 1;
      playerRow.gf += playerScore || 0;
      playerRow.ga += oppScore || 0;
      playerRow.pts += playerWon ? 3 : (playerScore === oppScore ? 1 : 0);
    }
    const otherOpps = grp.filter(r => !r.isPlayer && r.teamId !== fixture.homeTeamId && r.teamId !== fixture.awayTeamId);
    if (otherOpps.length >= 2) {
      const a = otherOpps[0], b = otherOpps[1];
      const aGoals = Math.floor(Math.random() * 4);
      const bGoals = Math.floor(Math.random() * 4);
      a.played += 1; b.played += 1;
      a.gf += aGoals; a.ga += bGoals;
      b.gf += bGoals; b.ga += aGoals;
      if (aGoals > bGoals) a.pts += 3;
      else if (bGoals > aGoals) b.pts += 3;
      else { a.pts += 1; b.pts += 1; }
    }
    cwc.group = grp;
    if (fixture.stage === 'group') {
      cwc.groupPlayed = (cwc.groupPlayed || 0) + 1;
      if (playerWon) cwc.groupPoints += 3;
      else if (fixture.homeScore === fixture.awayScore) cwc.groupPoints += 1;
      cwc.groupGF = (cwc.groupGF || 0) + (playerScore || 0);
      cwc.groupGA = (cwc.groupGA || 0) + (oppScore || 0);
      if (cwc.groupPlayed >= 3) {
        if (cwc.groupPoints >= 4) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            cwc.fixtures.push({
              id: `cwc-ko0-${Date.now()}`,
              week: fixture.week + 4,
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: 'CWC SEMI-FINAL',
              competition: 'cwc',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          cwc.eliminated = true;
        }
      }
    } else if (fixture.stage === 'ko') {
      if (!playerWon) {
        cwc.eliminated = true;
      } else {
        cwc.currentKoIdx = (cwc.currentKoIdx || 0) + 1;
        if (cwc.currentKoIdx < 2) {
          const nextOpp = pickFromPool(oppPool, usedIds);
          if (nextOpp) {
            cwc.fixtures.push({
              id: `cwc-ko${cwc.currentKoIdx}-${Date.now()}`,
              week: fixture.week + 3,
              homeTeamId: Math.random() < 0.5 ? careerState.playerTeamId : nextOpp.id,
              awayTeamId: Math.random() < 0.5 ? nextOpp.id : careerState.playerTeamId,
              round: 'CWC FINAL',
              competition: 'cwc',
              played: false,
              stage: 'ko'
            });
          }
        } else {
          cwc.won = true;
        }
      }
    }
    comps.cwc = cwc;
  }
  return comps;
};

// Get upcoming bonus fixtures (cup, UCL, UEL, UECL, CWC, WC) for player's team
export const getUpcomingBonusFixtures = (careerState) => {
  const comps = careerState.competitions || {};
  const all = [];
  ['cup', 'ucl', 'uel', 'uecl', 'cwc', 'wc'].forEach(k => {
    const c = comps[k];
    if (!c?.fixtures) return;
    c.fixtures.forEach(f => { if (!f.played) all.push(f); });
  });
  return all.sort((a, b) => a.week - b.week);
};

// Build the initial career state when user picks a player
export const createPlayerCareerState = (teams, playerTeamId, playerId) => {
  const clonedTeams = teams.map(t => ({ ...t, players: t.players.map(p => ({ ...p, stats: EMPTY_STATS(), growthPoints: 0 })), stats: { played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 } }));
  const fixtures = generateSeasonFixtures(clonedTeams);
  const playerTeam = clonedTeams.find(t => t.id === playerTeamId);
  const player = playerTeam?.players.find(p => p.id === playerId);
  return {
    mode: 'player_career',
    teams: clonedTeams,
    fixtures,
    competitions: buildCareerCompetitions(clonedTeams, playerTeamId),
    totwHistory: [], // weekly team of the week
    seasonAwards: [], // per-season awards archive
    currentWeek: 1,
    season: 1,
    year: START_YEAR,
    playerId,
    playerTeamId,
    playerName: player?.name || 'Unknown',
    playerAge: player?.age || 20,
    playerPosition: player?.position || 'CM',
    playerStartOverall: player?.overall || 70,
    matchHistory: [],
    seasonArchive: [],
    international: { caps: 0, goals: 0, assists: 0, matches: [] },
    pendingTransfer: null,
    createdAt: new Date().toISOString()
  };
};

// ============================================================
// LIVE MATCH TICKER — generate full 90-minute match timeline
// Interleaves player decision moments with auto-simulated events
// ============================================================

// Generate 0-5 auto events between minute `from` and `to`, skipping player team's decision moments
export const generateAutoMatchEvents = (home, away, playerTeam, minuteFrom, minuteTo) => {
  const events = [];
  const duration = minuteTo - minuteFrom;
  if (duration <= 0) return events;
  const homeOvr = getTeamOverall(home) || 75;
  const awayOvr = getTeamOverall(away) || 75;
  // Probability of events scales with duration
  const chance = Math.min(0.7, duration * 0.06);
  const commentaries = [
    'builds up patiently from the back.',
    'loses possession in midfield.',
    'wins a throw-in on the touchline.',
    'dangerous cross into the box — cleared.',
    'switches play to the other flank.',
    'a crunching tackle in midfield!',
    'shot from distance — over the bar.',
    'corner won after a deflection.',
    'the crowd erupts as a long pass finds its target.',
    'GK pulls off a comfortable save.',
    'offside flag goes up.',
    'free kick awarded just outside the box.',
    'smart interception breaks up the attack.',
    'pacey counter-attack developing!',
    'brilliant footwork beats a defender.',
    'pinpoint pass breaks the defensive line.'
  ];
  let m = minuteFrom;
  while (m < minuteTo) {
    m += 2 + Math.floor(Math.random() * 5);
    if (m >= minuteTo) break;
    if (Math.random() < chance) {
      const whichTeam = Math.random() < (homeOvr / (homeOvr + awayOvr)) ? home : away;
      events.push({
        minute: m,
        type: 'commentary',
        team: whichTeam.id === home.id ? 'home' : 'away',
        teamName: whichTeam.name,
        commentary: `${whichTeam.name} ${commentaries[Math.floor(Math.random() * commentaries.length)]}`
      });
    }
  }
  return events;
};

// Generate teammate/opponent goals for the full match (excluding goals from player decisions)
// Returns events with minute, team (home/away), scorer, assister
// Also generates a sprinkle of yellow/red cards so match feed feels alive.
export const generateMatchGoalEvents = (home, away, playerTeamId, plannedPlayerGoals, plannedPlayerAssists) => {
  const events = [];
  const homeOvr = getTeamOverall(home) || 75;
  const awayOvr = getTeamOverall(away) || 75;
  const addGoalsFor = (team, ownOvr, oppOvr, excludePlayer, isHomeSide) => {
    const diff = ownOvr - oppOvr;
    // Higher base expected goals + bigger rating advantage + tiny home pulse (+0.10)
    let expected = Math.max(0.3, 1.35 + diff * 0.075);
    if (isHomeSide) expected += 0.10;
    const n = Math.min(5, Math.floor(expected + Math.random() * 1.3));
    const teamGoals = [];
    for (let i = 0; i < n; i++) {
      const minute = 8 + Math.floor(Math.random() * 82);
      // pick a scorer (not the career player)
      const candidates = team.players.filter(p => p.position !== 'GK' && p.id !== excludePlayer);
      const scorer = candidates[Math.floor(Math.random() * candidates.length)] || candidates[0];
      if (!scorer) continue;
      const assistCandidates = candidates.filter(c => c.id !== scorer.id);
      const assister = Math.random() < 0.6 && assistCandidates.length ? assistCandidates[Math.floor(Math.random() * assistCandidates.length)] : null;
      teamGoals.push({
        minute,
        type: 'goal',
        team: team.id === home.id ? 'home' : 'away',
        teamName: team.name,
        scorer: scorer.name,
        assister: assister?.name,
        commentary: `GOAL! ${scorer.name} scores for ${team.name}${assister ? ` (assist: ${assister.name})` : ''}!`
      });
    }
    return teamGoals;
  };
  const addCardsFor = (team) => {
    // ~2.4 yellows / 0.12 reds per side per match on average — realistic top-flight rates.
    const yellowCount = Math.random() < 0.18 ? 0 : 1 + Math.floor(Math.random() * 3);
    const redCount = Math.random() < 0.08 ? 1 : 0;
    const teamCards = [];
    const candidates = team.players.filter(p => p.position !== 'GK');
    for (let i = 0; i < yellowCount; i++) {
      const minute = 12 + Math.floor(Math.random() * 78);
      const offender = candidates[Math.floor(Math.random() * candidates.length)];
      if (!offender) continue;
      teamCards.push({
        minute, type: 'yellow_card',
        team: team.id === home.id ? 'home' : 'away', teamName: team.name,
        scorer: offender.name,
        commentary: `🟨 Yellow card — ${offender.name} (${team.name}) booked for a foul.`
      });
    }
    for (let i = 0; i < redCount; i++) {
      const minute = 35 + Math.floor(Math.random() * 55);
      const offender = candidates[Math.floor(Math.random() * candidates.length)];
      if (!offender) continue;
      teamCards.push({
        minute, type: 'red_card',
        team: team.id === home.id ? 'home' : 'away', teamName: team.name,
        scorer: offender.name,
        commentary: `🟥 RED CARD! ${offender.name} (${team.name}) is sent off!`
      });
    }
    return teamCards;
  };
  // We'll regenerate when we know player goals are counted — strip career player scorer
  const playerTeam = [home, away].find(t => t.id === playerTeamId);
  const playerId = playerTeam?.players?.find(p => p.id === playerTeamId)?.id;
  events.push(...addGoalsFor(home, homeOvr, awayOvr, home.id === playerTeamId ? playerId : null, true));
  events.push(...addGoalsFor(away, awayOvr, homeOvr, away.id === playerTeamId ? playerId : null, false));
  events.push(...addCardsFor(home));
  events.push(...addCardsFor(away));
  return events.sort((a, b) => a.minute - b.minute);
};

// ============================================================
// INTERNATIONAL CALL-UPS
// ============================================================

// WC qualifying nations (from worldCup.js nations)
export const INTERNATIONAL_NATIONS = [
  'England', 'Scotland', 'Wales', 'Ireland', 'France', 'Spain', 'Germany', 'Italy',
  'Portugal', 'Netherlands', 'Belgium', 'Brazil', 'Argentina', 'Uruguay', 'Croatia',
  'Morocco', 'Senegal', 'Japan', 'South Korea', 'USA', 'Mexico', 'Canada',
  'Poland', 'Switzerland', 'Denmark', 'Serbia', 'Nigeria', 'Egypt', 'Ghana',
  'Ecuador', 'Colombia', 'Australia'
];

export const isEligibleForCallup = (player) => {
  return INTERNATIONAL_NATIONS.includes(player?.nationality);
};

// Decide if player is called up for an international window (4 per season)
// Probability rises with overall and with recent form
export const evaluateCallup = (player, recentRating) => {
  if (!isEligibleForCallup(player)) return false;
  const base = 0.15 + (player.overall - 70) * 0.035;
  const formBonus = Math.max(0, (recentRating - 6.8) * 0.12);
  const prob = Math.min(0.85, base + formBonus);
  return Math.random() < prob;
};

// Simulate an international match — generates cap + possibly goal/assist + rating
export const simulateInternationalMatch = (player, week, season, opponent) => {
  const isAttacker = ['ST','CF','LW','RW','CAM','SS','LF','RF'].includes(player.position);
  const isMid = ['CM','CDM','LM','RM'].includes(player.position);
  const goalChance = isAttacker ? 0.32 : isMid ? 0.12 : 0.05;
  const assistChance = isAttacker ? 0.22 : isMid ? 0.28 : 0.10;
  const goals = Math.random() < goalChance ? 1 : 0;
  const assists = Math.random() < assistChance ? 1 : 0;
  const rating = Math.max(5.5, Math.min(9.8, 6.5 + goals * 0.9 + assists * 0.5 + (Math.random() - 0.5) * 0.8));
  return {
    week, season,
    opponent,
    goals,
    assists,
    rating: Math.round(rating * 10) / 10,
    homeScore: goals + Math.floor(Math.random() * 3),
    awayScore: Math.floor(Math.random() * 3)
  };
};

// Get a random international opponent for a friendly / qualifier
const INTL_OPPONENT_POOL = ['Brazil', 'Argentina', 'Germany', 'Spain', 'France', 'Italy', 'Netherlands', 'Portugal', 'Belgium', 'Croatia', 'Uruguay', 'Morocco', 'Senegal', 'Japan', 'South Korea', 'USA', 'Mexico', 'Colombia'];
export const pickIntlOpponent = (playerNation) => {
  const pool = INTL_OPPONENT_POOL.filter(n => n !== playerNation);
  return pool[Math.floor(Math.random() * pool.length)];
};

// International window triggers at weeks: 10, 19, 28, 37 (4 per season)
export const INTERNATIONAL_WEEKS = [10, 19, 28, 37];
export const shouldTriggerIntlWindow = (weekAfterMatch) => INTERNATIONAL_WEEKS.includes(weekAfterMatch);

// Apply international match outcome to career state
export const applyInternationalMatch = (careerState, intlMatch) => {
  const team = careerState.teams.find(t => t.id === careerState.playerTeamId);
  if (!team) return careerState;
  const playerIdx = team.players.findIndex(p => p.id === careerState.playerId);
  if (playerIdx < 0) return careerState;
  const p = { ...team.players[playerIdx] };
  // growth bonus from good international form — slower
  const delta = (intlMatch.rating - 6.8) * 0.08;
  const gp = (p.growthPoints || 0) + delta;
  let overall = p.overall;
  let gpRem = gp;
  while (gpRem >= 1 && overall < (p.potential || 99)) { overall += 1; gpRem -= 1; }
  p.overall = overall;
  p.growthPoints = gpRem;
  const newTeams = [...careerState.teams];
  const teamIdx = newTeams.findIndex(t => t.id === team.id);
  const newPlayers = [...team.players];
  newPlayers[playerIdx] = p;
  newTeams[teamIdx] = { ...team, players: newPlayers };
  const international = careerState.international || { caps: 0, goals: 0, assists: 0, matches: [] };
  return {
    ...careerState,
    teams: newTeams,
    international: {
      caps: (international.caps || 0) + 1,
      goals: (international.goals || 0) + intlMatch.goals,
      assists: (international.assists || 0) + intlMatch.assists,
      matches: [...(international.matches || []), intlMatch]
    }
  };
};

// ============================================================
// TRANSFER MARKET — end-of-season offers
// ============================================================

// Generate 2-4 offers from other EWFL clubs based on player's season performance
export const generateTransferOffers = (careerState) => {
  const summary = getCareerSummary(careerState);
  if (!summary) return [];
  const { player, team, totalGoals, totalAssists, avgRating } = summary;
  // Performance score
  const perf = totalGoals * 3 + totalAssists * 2 + (avgRating - 6.5) * 10;
  // If player underperformed severely, NO offers (player must stay or be released) — rare
  if (perf < -5 && Math.random() < 0.5) return [];

  const otherTeams = careerState.teams.filter(t => t.id !== team.id);
  // Ranked teams with higher rank = more attractive
  const standings = calculateStandings(careerState.teams);
  // Number of offers depends on performance
  const baseOffers = perf >= 20 ? 4 : perf >= 8 ? 3 : 2;
  const offerCount = Math.min(4, Math.max(1, baseOffers + (Math.random() < 0.3 ? 1 : 0)));

  const picks = [];
  const usedIds = new Set();
  // Prefer top-half clubs if player had strong season, else pick from any
  const preferredPool = perf >= 15
    ? standings.slice(0, 10).filter(s => s.id !== team.id)
    : otherTeams;

  while (picks.length < offerCount && picks.length < preferredPool.length) {
    const candidate = preferredPool[Math.floor(Math.random() * preferredPool.length)];
    if (usedIds.has(candidate.id)) continue;
    usedIds.add(candidate.id);
    picks.push(candidate);
  }

  return picks.map(t => {
    const rank = standings.findIndex(s => s.id === t.id) + 1;
    const teamOvr = getTeamOverall(t);
    // Wage / prestige based on rank
    const prestige = rank <= 2 ? 'Elite' : rank <= 6 ? 'Top' : rank <= 12 ? 'Mid' : 'Low';
    const baseWage = 40 + Math.floor((21 - rank) * 4) + Math.floor(player.overall * 0.6);
    const wage = baseWage + Math.floor(Math.random() * 15);
    return {
      teamId: t.id,
      teamName: t.name,
      teamCity: t.city,
      rank,
      teamOvr,
      prestige,
      wage, // in thousands/week
      contractYears: [2, 3, 4, 5][Math.floor(Math.random() * 4)],
      signingBonus: Math.floor(wage * 10 + Math.random() * 300)
    };
  }).sort((a, b) => a.rank - b.rank);
};

// Accept a transfer offer — move player to the new team
export const acceptTransferOffer = (careerState, offer) => {
  const fromTeam = careerState.teams.find(t => t.id === careerState.playerTeamId);
  const toTeam = careerState.teams.find(t => t.id === offer.teamId);
  if (!fromTeam || !toTeam) return careerState;
  const player = fromTeam.players.find(p => p.id === careerState.playerId);
  if (!player) return careerState;

  // Remove from old team
  const newFromTeam = { ...fromTeam, players: fromTeam.players.filter(p => p.id !== player.id) };
  // Add to new team (swap with a similar-position squad player to keep roster size stable)
  const similar = toTeam.players.find(p => p.position === player.position && p.id !== player.id);
  let newToPlayers = [...toTeam.players];
  if (similar) {
    newToPlayers = newToPlayers.filter(p => p.id !== similar.id);
  }
  // Assign next available shirt number in new team
  const usedNumbers = new Set(newToPlayers.map(p => p.number));
  let newNumber = player.number;
  if (usedNumbers.has(newNumber)) {
    for (let i = 1; i <= 99; i++) if (!usedNumbers.has(i)) { newNumber = i; break; }
  }
  const movedPlayer = { ...player, number: newNumber };
  newToPlayers.push(movedPlayer);

  // Rebuild new team's lineup with new player
  const newToTeamBase = { ...toTeam, players: newToPlayers };
  const updated = careerState.teams.map(t => {
    if (t.id === fromTeam.id) return newFromTeam;
    if (t.id === toTeam.id) return newToTeamBase;
    return t;
  });

  return {
    ...careerState,
    teams: updated,
    playerTeamId: toTeam.id,
    pendingTransfer: null,
    transferHistory: [...(careerState.transferHistory || []), {
      season: careerState.season,
      year: careerState.year,
      fromTeam: fromTeam.name,
      toTeam: toTeam.name,
      wage: offer.wage,
      signingBonus: offer.signingBonus,
      contractYears: offer.contractYears
    }]
  };
};

// Summarise current season for the player (used in career dashboard)
export const getCareerSummary = (careerState) => {
  const team = careerState.teams.find(t => t.id === careerState.playerTeamId);
  const player = team?.players.find(p => p.id === careerState.playerId);
  if (!player) return null;
  const standings = calculateStandings(careerState.teams);
  const rank = standings.findIndex(t => t.id === team.id) + 1;
  const matches = careerState.matchHistory.filter(m => m.season === (careerState.season || 1));
  const totalRating = matches.reduce((s, m) => s + (m.playerRating || 0), 0);
  const avgRating = matches.length ? (totalRating / matches.length) : 0;
  return {
    team,
    player,
    rank,
    matches,
    avgRating,
    totalGoals: matches.reduce((s, m) => s + (m.playerGoals || 0), 0),
    totalAssists: matches.reduce((s, m) => s + (m.playerAssists || 0), 0)
  };
};

// Archive current season & advance to the next season
// Returns the updated state with a `pendingTransfer` block if offers were generated
export const advanceCareerSeason = (careerState) => {
  const summary = getCareerSummary(careerState);
  const archive = [...(careerState.seasonArchive || [])];
  if (summary) {
    archive.push({
      season: careerState.season || 1,
      year: careerState.year,
      teamId: careerState.playerTeamId,
      teamName: summary.team.name,
      finalRank: summary.rank,
      matches: summary.matches.length,
      goals: summary.totalGoals,
      assists: summary.totalAssists,
      avgRating: summary.avgRating,
      endOverall: summary.player.overall,
      cupWon: careerState.competitions?.cup?.won || false,
      uclWon: careerState.competitions?.ucl?.won || false
    });
  }
  // Generate transfer offers BEFORE resetting teams
  const offers = generateTransferOffers(careerState);

  // Reset league stats for new season, age all players
  const teams = careerState.teams.map(t => ({
    ...t,
    stats: { played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 },
    players: t.players.map(p => ({ ...p, age: (p.age || 20) + 1, stats: EMPTY_STATS(), seasonStartOverall: p.overall }))
  }));
  const fixtures = generateSeasonFixtures(teams);
  const playerTeam = teams.find(t => t.id === careerState.playerTeamId);
  const player = playerTeam?.players.find(p => p.id === careerState.playerId);
  // Rebuild competitions for new season using player's new rank (summary.rank)
  const competitions = buildCareerCompetitions(teams, careerState.playerTeamId, summary?.rank);
  return {
    ...careerState,
    teams, fixtures,
    competitions,
    currentWeek: 1,
    season: (careerState.season || 1) + 1,
    year: (careerState.year || START_YEAR) + 1,
    playerAge: player?.age || careerState.playerAge,
    seasonArchive: archive,
    matchHistory: [],
    pendingTransfer: offers.length ? { offers, generatedAt: new Date().toISOString() } : null
  };
};
